


import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, TextField, Divider, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, Box,
       Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,InputLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const OutCome = () => {
  const [date, setDate] = useState('');
  const [formData, setFormData] = useState({
    audiogramRows: Array(4).fill().map(() => ({
      device: '',
      ear: '',
      condition: '',
      frequencies: { hz250: '', hz500: '', hz1k: '', hz1_5k: '', hz2k: '', hz3k: '', hz4k: '', hz6k: '', hz8k: '' }
    })),
    lingSoundAwareness: Array(4).fill().map(() => ({
      device: '',
      ear: '',
      condition: '', 
      a: '',
      i: '',
      u: '',
      m: '',
      s: '',
      sh: ''
    })),
    lingSoundIdentification: Array(4).fill().map(() => ({
      device: '',
      ear: '',
      condition: '',
      a: '',
      i: '',
      u: '',
      m: '',
      s: '',
      sh: ''
    })),
    speechIdentification: Array(4).fill().map(() => ({
      device: '',
      ear: '',
      condition: '',
      open: '',
      closed: ''
    })),
    espRows: Array(6).fill().map(() => ({
      device: '',
      ear: '',
      condition: '',
      test: '',
      AV: '',
      A1: '',
      A2: ''
    })),
    mais: { score: '' },
    questionnaires: {
      reels: { rla: '', ela: '' },
      secs: { crla: '', cela: '' },
      integratedScaleOfDevelopment: {
        rl: '',
        el: '',
        listening: '',
        speech: '',
        cognitiveSkills: '',
        socialCommunication: '',
      }
    }
  });

  const [searchOpNumber, setSearchOpNumber] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
   const [loading, setLoading] = useState(false);
   const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
   const [openPasswordModal, setOpenPasswordModal] = useState(false);
   const [password, setPassword] = useState('');
   const [downloadFormat, setDownloadFormat] = useState('excel');
  const [error, setError] = useState('');
  const [savedEspData, setSavedEspData] = useState([]);

  // ---------- helper to reset ESP rows ----------
  const defaultEspRows = () => Array(6).fill().map(() => ({
    device: '',
    ear: '',
    condition: '',
    test: '',
    AV: '',
    A1: '',
    A2: ''
  }));

  // ---------- Load saved ESP data when patient is searched ----------
  useEffect(() => {
    if (searchOpNumber.trim() && isFormVisible) {
      loadSavedEspData();
    }
  }, [searchOpNumber, isFormVisible]);

  const normalizeEspResponse = (raw) => {
    if (!raw) return [];
    if (raw.espData !== undefined) raw = raw.espData;
    if (Array.isArray(raw)) {
      if (raw.every(el => Array.isArray(el))) return raw;
      return raw.map(el => {
        if (Array.isArray(el)) return el;
        if (el && Array.isArray(el.espRows)) return el.espRows;
        if (el && typeof el === 'object') return [el];
        return [];
      });
    }
    if (typeof raw === 'object') {
      if (Array.isArray(raw.espRows)) return [raw.espRows];
      return [[raw]];
    }
    return [];
  };
  const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Outcome CI ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "outcome_ci",
          formName: "Outcome_CI",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Outcome_CI_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };
  const loadSavedEspData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/outcome/${searchOpNumber.trim()}/esp`);
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded ESP data (raw):', data);
        const normalized = normalizeEspResponse(data);
        setSavedEspData(normalized);
      } else {
        setSavedEspData([]);
      }
    } catch (error) {
      console.log('No existing ESP data found or fetch failed:', error);
      setSavedEspData([]);
    }
  };

  // ---------- Search logic ----------
  const handleSearch = async () => {
    if (!searchOpNumber.trim()) {
      setError('Please enter an OP number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/outcome/search/${searchOpNumber.trim()}`);

      if (response.ok) {
        const patientData = await response.json();
        setSearchResult(patientData);
        setIsFormVisible(true);

        try {
          const existingDataResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/outcome/${searchOpNumber.trim()}`);
          if (existingDataResponse.ok) {
            const existingData = await existingDataResponse.json();
            console.log('Existing outcome data found:', existingData);

            const mapOutcomeData = (dbData) => {
              const audiogramRows = [1,2,3,4].map(i => ({
                device: dbData[`audiogram_device_${i}`] || '',
                ear: dbData[`audiogram_ear_${i}`] || '',
                condition: dbData[`audiogram_condition_${i}`] || '',
                frequencies: {
                  hz250: dbData[`audiogram_hz250_${i}`] || '',
                  hz500: dbData[`audiogram_hz500_${i}`] || '',
                  hz1k: dbData[`audiogram_hz1k_${i}`] || '',
                  hz1_5k: dbData[`audiogram_hz1_5k_${i}`] || '',
                  hz2k: dbData[`audiogram_hz2k_${i}`] || '',
                  hz3k: dbData[`audiogram_hz3k_${i}`] || '',
                  hz4k: dbData[`audiogram_hz4k_${i}`] || '',
                  hz6k: dbData[`audiogram_hz6k_${i}`] || '',
                  hz8k: dbData[`audiogram_hz8k_${i}`] || ''
                }
              }));

              const takeLing = (prefix) => Array(4).fill().map((_, idx) => ({
                device: dbData[`${prefix}_device_${idx+1}`] || '',
                ear: dbData[`${prefix}_ear_${idx+1}`] || '',
                condition: dbData[`${prefix}_condition_${idx+1}`] || '',
                a: dbData[`${prefix}_a_${idx+1}`] || '',
                i: dbData[`${prefix}_i_${idx+1}`] || '',
                u: dbData[`${prefix}_u_${idx+1}`] || '',
                m: dbData[`${prefix}_m_${idx+1}`] || '',
                s: dbData[`${prefix}_s_${idx+1}`] || '',
                sh: dbData[`${prefix}_sh_${idx+1}`] || ''
              }));

              const lingAw = takeLing('ling_awareness');
              const lingId = takeLing('ling_identification');

              const speech = Array(4).fill().map((_, idx) => ({
                device: dbData[`speech_identification_device_${idx+1}`] || '',
                ear: dbData[`speech_identification_ear_${idx+1}`] || '',
                condition: dbData[`speech_identification_condition_${idx+1}`] || '',
                open: dbData[`speech_identification_open_${idx+1}`] || '',
                closed: dbData[`speech_identification_closed_${idx+1}`] || ''
              }));

              return {
                audiogramRows,
                lingSoundAwareness: lingAw,
                lingSoundIdentification: lingId,
                speechIdentification: speech,
                mais: { score: dbData.mais_score || '' },
                questionnaires: {
                  reels: { rla: dbData.reels_rla || '', ela: dbData.reels_ela || '' },
                  secs: { crla: dbData.secs_crla || '', cela: dbData.secs_cela || '' },
                  integratedScaleOfDevelopment: {
                    rl: dbData.isd_rl || '',
                    el: dbData.isd_el || '',
                    listening: dbData.isd_listening || '',
                    speech: dbData.isd_speech || '',
                    cognitiveSkills: dbData.isd_cognitive_skills || '',
                    socialCommunication: dbData.isd_social_communication || '',
                  }
                }
              };
            };

            const mappedData = mapOutcomeData(existingData);
            setFormData(prev => ({ ...prev, ...mappedData }));
            setDate(existingData.date_of_assessment || '');
          }
        } catch (error) {
          console.log('No existing data found, starting with fresh form');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Patient not found. Please check the OP number.');
        setSearchResult(null);
        setIsFormVisible(false);
      }
    } catch (error) {
      console.error('Error searching for patient:', error);
      setError('Error searching for patient. Please check your connection.');
      setSearchResult(null);
      setIsFormVisible(false);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Full form submit ----------
  const handleSubmit = async () => {
    if (!searchOpNumber.trim()) {
      setError('Please search for a patient first');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/outcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opNumber: searchOpNumber.trim(),
          dateOfAssessment: date,
          ...formData
        }),
      });

      if (response.ok) {
        const result = await response.json();
        alert('Outcome data saved successfully!');
        console.log('Saved data:', result.data);
      } else {
        const error = await response.json();
        setError(`Error saving data: ${error.error}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  // ---------- ESP Save (appends to existing ESP data) ----------
  const handleEspSave = async () => {
    if (!searchOpNumber.trim()) {
      setError('Please search for a patient first');
      return;
    }

    const hasData = formData.espRows.some(row => 
      row.device || row.ear || row.condition || row.test || row.AV || row.A1 || row.A2
    );

    if (!hasData) {
      setError('Please fill at least one ESP row before saving');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const espRowsToSave = formData.espRows.filter(row => 
        row.device || row.ear || row.condition || row.test || row.AV || row.A1 || row.A2
      );

      console.log('Saving ESP data:', espRowsToSave);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/outcome/esp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opNumber: searchOpNumber.trim(),
          dateOfAssessment: date,
          espRows: espRowsToSave
        })
      });

      if (response.ok) {
        const res = await response.json();
        alert(`ESP data saved successfully! Total ESP sets: ${res.espCount}`);
        console.log('ESP save response:', res);

        setFormData(prev => ({
          ...prev,
          espRows: defaultEspRows()
        }));
        
        await loadSavedEspData();
        
        setError('');
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Error saving ESP');
      }
    } catch (err) {
      console.error('Error saving ESP:', err);
      setError('Error saving ESP. Check connection.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Handlers for form changes ----------
  const handleAudiogramRowChange = (index, field, value) => {
    setFormData(prev => {
      const newRows = [...prev.audiogramRows];
      newRows[index] = { ...newRows[index], [field]: value };
      return { ...prev, audiogramRows: newRows };
    });
  };

  const handleFrequencyChange = (index, freq, value) => {
    setFormData(prev => {
      const newRows = [...prev.audiogramRows];
      const newFrequencies = { ...newRows[index].frequencies, [freq]: value };
      newRows[index] = { ...newRows[index], frequencies: newFrequencies };
      return { ...prev, audiogramRows: newRows };
    });
  };

  const handleLingAwarenessRowChange = (index, field, value) => {
    setFormData(prev => {
      const newRows = [...prev.lingSoundAwareness];
      newRows[index] = { ...newRows[index], [field]: value };
      return { ...prev, lingSoundAwareness: newRows };
    });
  };

  const handleLingAwarenessSoundChange = (index, sound, value) => {
    setFormData(prev => {
      const newRows = [...prev.lingSoundAwareness];
      newRows[index] = { ...newRows[index], [sound]: value };
      return { ...prev, lingSoundAwareness: newRows };
    });
  };

  const handleLingIdentificationRowChange = (index, field, value) => {
    setFormData(prev => {
      const newRows = [...prev.lingSoundIdentification];
      newRows[index] = { ...newRows[index], [field]: value };
      return { ...prev, lingSoundIdentification: newRows };
    });
  };

  // keep this handler — we'll use it with textfields now
  const handleLingIdentificationSoundChange = (index, sound, value) => {
    setFormData(prev => {
      const newRows = [...prev.lingSoundIdentification];
      newRows[index] = { ...newRows[index], [sound]: value };
      return { ...prev, lingSoundIdentification: newRows };
    });
  };

  const handleSpeechRowChange = (index, field, value) => {
    setFormData(prev => {
      const newRows = [...prev.speechIdentification];
      newRows[index] = { ...newRows[index], [field]: value };
      return { ...prev, speechIdentification: newRows };
    });
  };

  const handleEspRowChange = (index, field, value) => {
    setFormData(prev => {
      const newRows = [...prev.espRows];
      newRows[index] = { ...newRows[index], [field]: value };
      return { ...prev, espRows: newRows };
    });
  };

  const handleChange = (path, value) => {
    const paths = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < paths.length - 1; i++) {
        const p = paths[i];
        if (!(p in current)) {
          return prev;
        }
        current[p] = Array.isArray(current[p]) ? [...current[p]] : { ...current[p] };
        current = current[p];
      }
      current[paths[paths.length - 1]] = value;
      return newData;
    });
  };

  // ---------- Component for displaying saved ESP data ----------
  const SavedEspData = () => {
    if (!savedEspData || savedEspData.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
          Saved ESP Data History
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {savedEspData.map((espSet, setIndex) => {
          const rows = Array.isArray(espSet)
            ? espSet
            : (espSet && Array.isArray(espSet.espRows) ? espSet.espRows : []);

          if (!rows || rows.length === 0) return null;

          return (
            <Box key={setIndex} sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                ESP Set #{setIndex + 1}
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Device</TableCell>
                      <TableCell>Ear</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell>Test</TableCell>
                      <TableCell>AV</TableCell>
                      <TableCell>A1</TableCell>
                      <TableCell>A2</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell>{row?.device ?? ''}</TableCell>
                        <TableCell>{row?.ear ?? ''}</TableCell>
                        <TableCell>{row?.condition ?? ''}</TableCell>
                        <TableCell>{row?.test ?? ''}</TableCell>
                        <TableCell>{row?.AV ?? ''}</TableCell>
                        <TableCell>{row?.A1 ?? ''}</TableCell>
                        <TableCell>{row?.A2 ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ---------- Updated helper components with dropdowns for Yes/No ----------
  const AudiogramRow = ({ index }) => (
    <TableRow>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.audiogramRows[index].device}
            onChange={e => handleAudiogramRowChange(index, 'device', e.target.value)}
          >
            <MenuItem value="CI">CI</MenuItem>
            <MenuItem value="HA">HA</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.audiogramRows[index].ear}
            onChange={e => handleAudiogramRowChange(index, 'ear', e.target.value)}
          >
            <MenuItem value="Right">Right</MenuItem>
            <MenuItem value="Left">Left</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.audiogramRows[index].condition}
            onChange={e => handleAudiogramRowChange(index, 'condition', e.target.value)}
          >
            <MenuItem value="Aided">Aided</MenuItem>
            <MenuItem value="Unaided">Unaided</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      {['hz250', 'hz500', 'hz1k', 'hz1_5k', 'hz2k', 'hz3k', 'hz4k', 'hz6k', 'hz8k'].map(freq => (
        <TableCell key={freq}>
          <TextField
            size="small"
            fullWidth
            value={formData.audiogramRows[index].frequencies[freq] || ''}
            onChange={e => handleFrequencyChange(index, freq, e.target.value)}
          />
        </TableCell>
      ))}
    </TableRow>
  );

  const LingSoundAwarenessRow = ({ index }) => (
    <TableRow>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.lingSoundAwareness[index].device || ''}
            onChange={e => handleLingAwarenessRowChange(index, 'device', e.target.value)}
          >
            <MenuItem value="CI">CI</MenuItem>
            <MenuItem value="HA">HA</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.lingSoundAwareness[index].condition || ''}
            onChange={e => handleLingAwarenessRowChange(index, 'condition', e.target.value)}
          >
            <MenuItem value="Aided">Aided</MenuItem>
            <MenuItem value="Unaided">Unaided</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.lingSoundAwareness[index].ear || ''}
            onChange={e => handleLingAwarenessRowChange(index, 'ear', e.target.value)}
          >
            <MenuItem value="Right">Right</MenuItem>
            <MenuItem value="Left">Left</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      {['a', 'i', 'u', 'm', 's', 'sh'].map(sound => (
        <TableCell key={sound}>
          <FormControl fullWidth size="small">
            <Select
              value={formData.lingSoundAwareness[index][sound] || ''}
              onChange={e => handleLingAwarenessSoundChange(index, sound, e.target.value)}
              displayEmpty
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </TableCell>
      ))}
    </TableRow>
  );

  // ---------- CHANGED: LingSoundIdentificationRow now uses TextField inputs (free text) ----------
  const LingSoundIdentificationRow = ({ index }) => (
    <TableRow>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.lingSoundIdentification[index].device || ''}
            onChange={e => handleLingIdentificationRowChange(index, 'device', e.target.value)}
          >
            <MenuItem value="CI">CI</MenuItem>
            <MenuItem value="HA">HA</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.lingSoundIdentification[index].condition || ''}
            onChange={e => handleLingIdentificationRowChange(index, 'condition', e.target.value)}
          >
            <MenuItem value="Aided">Aided</MenuItem>
            <MenuItem value="Unaided">Unaided</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.lingSoundIdentification[index].ear || ''}
            onChange={e => handleLingIdentificationRowChange(index, 'ear', e.target.value)}
          >
            <MenuItem value="Right">Right</MenuItem>
            <MenuItem value="Left">Left</MenuItem>
          </Select>
        </FormControl>
      </TableCell>

      {/* <-- free text fields instead of Yes/No dropdowns --> */}
      {['a', 'i', 'u', 'm', 's', 'sh'].map(sound => (
        <TableCell key={sound}>
          <TextField
            size="small"
            fullWidth
            
            value={formData.lingSoundIdentification[index][sound] || ''}
            onChange={e => handleLingIdentificationSoundChange(index, sound, e.target.value)}
          />
        </TableCell>
      ))}
    </TableRow>
  );

  const SpeechIdentificationRow = ({ index }) => (
    <TableRow>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.speechIdentification[index].device || ''}
            onChange={e => handleSpeechRowChange(index, 'device', e.target.value)}
          >
            <MenuItem value="CI">CI</MenuItem>
            <MenuItem value="HA">HA</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.speechIdentification[index].condition || ''}
            onChange={e => handleSpeechRowChange(index, 'condition', e.target.value)}
          >
            <MenuItem value="Aided">Aided</MenuItem>
            <MenuItem value="Unaided">Unaided</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.speechIdentification[index].ear || ''}
            onChange={e => handleSpeechRowChange(index, 'ear', e.target.value)}
          >
            <MenuItem value="Right">Right</MenuItem>
            <MenuItem value="Left">Left</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={formData.speechIdentification[index].open || ''}
          onChange={e => handleSpeechRowChange(index, 'open', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={formData.speechIdentification[index].closed || ''}
          onChange={e => handleSpeechRowChange(index, 'closed', e.target.value)}
        />
      </TableCell>
    </TableRow>
  );

  const ESPRow = ({ index }) => (
    <TableRow>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.espRows[index].device}
            onChange={e => handleEspRowChange(index, 'device', e.target.value)}
          >
            <MenuItem value="CI">CI</MenuItem>
            <MenuItem value="HA">HA</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.espRows[index].ear}
            onChange={e => handleEspRowChange(index, 'ear', e.target.value)}
          >
            <MenuItem value="Right">Right</MenuItem>
            <MenuItem value="Left">Left</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.espRows[index].condition}
            onChange={e => handleEspRowChange(index, 'condition', e.target.value)}
          >
            <MenuItem value="Aided">Aided</MenuItem>
            <MenuItem value="Unaided">Unaided</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small">
          <Select
            value={formData.espRows[index].test}
            onChange={e => handleEspRowChange(index, 'test', e.target.value)}
          >
            <MenuItem value="Syllable">Syllable Categorisation</MenuItem>
            <MenuItem value="Bisyllable">Word Identification</MenuItem>
            <MenuItem value="Vowel">Vowel Identification</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={formData.espRows[index].AV}
          onChange={e => handleEspRowChange(index, 'AV', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={formData.espRows[index].A1}
          onChange={e => handleEspRowChange(index, 'A1', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={formData.espRows[index].A2}
          onChange={e => handleEspRowChange(index, 'A2', e.target.value)}
        />
      </TableCell>
    </TableRow>
  );

  // ---------- Render ----------
  return (
    <Paper elevation={3} sx={{ p: 4, margin: 'auto', maxWidth: 1600, mt: 4, mb: 6 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 4, color: '#1976d2' }}>
        Outcome Evaluation
      </Typography>

      {/* Search Section */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            label="Search OP Number"
            value={searchOpNumber}
            onChange={e => setSearchOpNumber(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading}
            sx={{ height: '40px' }}
          >
            {loading ? 'Searching...' : 'Submit'}
          </Button>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Demographics */}
      {searchResult && (
        <Paper elevation={1} sx={{ p: 2, mb: 4, bgcolor: '#e3f2fd' }}>
            <Box sx={{ position: 'absolute', top: 350, right: 140 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
          <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
            Demographic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Name:</strong> {searchResult.name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Age:</strong> {searchResult.age || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>IP Number:</strong> {searchResult.ip_number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>OP Number:</strong> {searchResult.op_number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Phone:</strong> {searchResult.phone || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography><strong>Address:</strong> {searchResult.address || 'N/A'}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {isFormVisible && (
        <Grid container spacing={4}>
          <Box mb={4} border={1} borderColor="divider" p={2} borderRadius={2} width="100%">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="dateOfAssessment"
                label="Date of Assessment"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Box>

          {/* Audiogram Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
              Audiogram Results
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Ear</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>250Hz</TableCell>
                    <TableCell>500Hz</TableCell>
                    <TableCell>1kHz</TableCell>
                    <TableCell>1.5kHz</TableCell>
                    <TableCell>2kHz</TableCell>
                    <TableCell>3kHz</TableCell>
                    <TableCell>4kHz</TableCell>
                    <TableCell>6kHz</TableCell>
                    <TableCell>8kHz</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.audiogramRows.map((row, index) => (
                    <AudiogramRow key={index} index={index} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Ling Sound Awareness */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
              Ling Sound Awareness
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Ear</TableCell>
                    <TableCell>/a/</TableCell>
                    <TableCell>/i/</TableCell>
                    <TableCell>/u/</TableCell>
                    <TableCell>/m/</TableCell>
                    <TableCell>/s/</TableCell>
                    <TableCell>/sh/</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lingSoundAwareness.map((_, idx) => (
                    <LingSoundAwarenessRow key={idx} index={idx} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Ling Sound Identification */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
              Ling Sound Identification
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Ear</TableCell>
                    <TableCell>/a/</TableCell>
                    <TableCell>/i/</TableCell>
                    <TableCell>/u/</TableCell>
                    <TableCell>/m/</TableCell>
                    <TableCell>/s/</TableCell>
                    <TableCell>/sh/</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lingSoundIdentification.map((_, idx) => (
                    <LingSoundIdentificationRow key={idx} index={idx} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Speech Identification */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
              Speech Identification
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Ear</TableCell>
                    <TableCell>Open Set</TableCell>
                    <TableCell>Closed Set</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.speechIdentification.map((_, idx) => (
                    <SpeechIdentificationRow key={idx} index={idx} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* ESP Scores */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
              ESP (Early Speech Perception) - Current Input
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Ear</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Test</TableCell>
                    <TableCell>AV</TableCell>
                    <TableCell>A1</TableCell>
                    <TableCell>A2</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.espRows.map((row, index) => (
                    <ESPRow key={index} index={index} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Save ESP Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" onClick={handleEspSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save ESP'}
              </Button>
            </Box>

            {/* Display Saved ESP Data */}
            <SavedEspData />
          </Grid>

          {/* MAIS and Questionnaires */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#555' }}>
              MAIS and Questionnaires
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* MAIS */}
            <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                MAIS
              </Typography>
              <TextField
                fullWidth
                label="Score"
                value={formData.mais.score}
                onChange={e => handleChange('mais.score', e.target.value)}
              />
            </Box>

            {/* REELS */}
            <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                REELS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="RLA"
                    value={formData.questionnaires.reels.rla}
                    onChange={e => handleChange('questionnaires.reels.rla', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="ELA"
                    value={formData.questionnaires.reels.ela}
                    onChange={e => handleChange('questionnaires.reels.ela', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* SECS */}
            <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                SECS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="CRLA"
                    value={formData.questionnaires.secs.crla}
                    onChange={e => handleChange('questionnaires.secs.crla', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="CELA"
                    value={formData.questionnaires.secs.cela}
                    onChange={e => handleChange('questionnaires.secs.cela', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Integrated Scale of Development */}
            <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Integrated Scale of Development
              </Typography>
              <Grid container spacing={2}>
                {['rl', 'el', 'listening', 'speech','cognitiveSkills', 'socialCommunication'].map(field => (
                  <Grid item xs={12} sm={6} md={4} key={field}>
                    <TextField
                      fullWidth
                      label={field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      value={formData.questionnaires.integratedScaleOfDevelopment[field]}
                      onChange={e => handleChange(`questionnaires.integratedScaleOfDevelopment.${field}`, e.target.value)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
   {/* Password Modal */}
      <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
          {/* Submit Button (full form) */}
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ px: 6, py: 1.5, fontSize: '1.1rem', minWidth: 250 }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default OutCome;
